/**
 * Per-group "Optimization enabled" + Method row for rule-based path groups.
 * Mirrors Voluum's group-level optimisation control.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useId, type FC } from 'react';
import type {
  VoluumCalculationMethod,
  VoluumPathGroupUpdate,
} from '@trafficpulse/shared';

import { Switch } from '@/components/ui/form/controls/Switch';
import { cn } from '@/lib/utils';
import { useTrackerAiHidden } from '@/features/flow-optimizer/tracker';

import {
  groupCalculationMethodUniform,
  groupOptimizationStats,
  groupOptimizationUniform,
  setGroupCalculationMethod,
  setGroupOptimizationEnabled,
} from '../rule-group-optimization';
import CalculationMethodSelect from '../../sections/paths-workspace/flow-controls/CalculationMethodSelect';

export interface RuleGroupOptimizationRowProps {
  group: VoluumPathGroupUpdate;
  groupName: string;
  onPatch: (mutator: (g: VoluumPathGroupUpdate) => void) => void;
}

const METHOD_TOOLTIP =
  'How Voluum scores path weights inside this rule group. Updating writes the same method to every path in the group.';

const RuleGroupOptimizationRow: FC<RuleGroupOptimizationRowProps> = ({
  group,
  groupName,
  onPatch,
}) => {
  const switchId = useId();
  // Hidden while TrafficPulse Optimisation (preview) is active — the tracker's
  // optimization is auto-managed then — and for a tracker without its own AI
  // (Tres Commas). Render-only; payload untouched.
  const hideAi = useTrackerAiHidden();
  if (hideAi) return null;
  const { total } = groupOptimizationStats(group);
  const disabled = total === 0;
  const uniform = groupOptimizationUniform(group);
  const switchOn = uniform === 'allOn';
  const method = groupCalculationMethodUniform(group);

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1.5 px-2 py-1',
        disabled && 'pointer-events-none opacity-50',
      )}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-2">
        <Switch
          id={switchId}
          checked={switchOn}
          disabled={disabled}
          onCheckedChange={(next) =>
            onPatch((g) => setGroupOptimizationEnabled(g, next))
          }
          aria-label={`Optimization enabled for ${groupName}`}
        />
        <label
          htmlFor={switchId}
          className="cursor-pointer text-[11px] font-medium text-foreground"
        >
          Optimization enabled
        </label>
      </div>

      {switchOn ? (
        <CalculationMethodSelect
          inlineRow
          value={method}
          disabled={disabled}
          onChange={(v: VoluumCalculationMethod) =>
            onPatch((g) => setGroupCalculationMethod(g, v))
          }
          ariaLabel={`Optimization method for ${groupName}`}
          tooltipText={METHOD_TOOLTIP}
        />
      ) : null}
    </div>
  );
};

export default RuleGroupOptimizationRow;
